import { Button, IconButton, Stack } from '@mui/material';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import * as uuid from 'uuid';

import { Text } from 'common';

import { BlockTextArrayField } from '../../../ConfigFields/CodeMirror/BlockTextArray/Field.tsx';
import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';

interface OrderDiagnosesProps {
  prefix: string;
}

export function OrderDiagnoses({ prefix }: OrderDiagnosesProps) {
  const { value: diagnoses, setValue: setDiagnoses } = useSelectedNodeField<
    { diagnosisId: string; description: string; icd10Codes: string[] | null }[] | string | undefined
  >(`${prefix}.diagnoses`);

  const showTextField = !diagnoses || typeof diagnoses === 'string' || diagnoses.length === 0;
  const showArrayEditor = !diagnoses || Array.isArray(diagnoses);

  return (
    <Stack gap={3}>
      <Text variant="heading5" bold>
        Diagnoses
      </Text>
      {showTextField && (
        <BlockValueTemplateField
          fieldName={`${prefix}.diagnoses`}
          label="Diagnoses"
          helperText="Enter the reference to diagnoses"
        />
      )}
      {showArrayEditor && (
        <>
          {Array.isArray(diagnoses) &&
            diagnoses?.map((diagnosis, index) => {
              const fieldNamePrefix = `${prefix}.diagnoses.${index}`;
              return (
                <Stack key={diagnosis.diagnosisId} spacing={2} padding={1} paddingLeft={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Text variant="heading5" bold>
                      Diagnosis {index + 1}
                    </Text>
                    <IconButton
                      aria-label="Delete Field"
                      size="medium"
                      onClick={() => {
                        const newDiagnoses = diagnoses.filter((_, i) => i !== index);
                        if (newDiagnoses.length === 0) {
                          setDiagnoses(undefined);
                        } else {
                          setDiagnoses(newDiagnoses);
                        }
                      }}
                    >
                      <TrashIcon size={16} />
                    </IconButton>
                  </Stack>
                  <BlockValueTemplateField
                    fieldName={`${fieldNamePrefix}.description`}
                    label="Description"
                    helperText="Enter the reference to the diagnosis's Description. e.g. `{{data.description}}`"
                    placeholder="e.g. {{data.description}}"
                  />
                  <BlockTextArrayField
                    fieldName={`${fieldNamePrefix}.icd10Codes`}
                    label="ICD-10 Codes"
                    addRowLabel="Add ICD-10 Code"
                    placeholder="Enter ICD-10 Code"
                  />
                </Stack>
              );
            })}
          <Button
            variant="contained"
            onClick={() => {
              const newDiagnosis = {
                diagnosisId: uuid.v4(),
                description: '',
                icd10Codes: [],
              };
              if (Array.isArray(diagnoses)) {
                setDiagnoses([...(diagnoses ?? []), newDiagnosis]);
              } else {
                setDiagnoses([newDiagnosis]);
              }
            }}
          >
            <PlusIcon size={12} />
            Add Diagnosis
          </Button>
        </>
      )}
    </Stack>
  );
}
